export const localText = (value, lang) =>
  typeof value === "string"
    ? value
    : value?.[lang] || value?.zh || value?.en || "";
export const safeLabUrl = (value) =>
  typeof value === "string" &&
  (/^https?:\/\//.test(value) || /^\/(?!\/)/.test(value)) &&
  !/[\r\n\\]/.test(value);
export function validateLabCatalog(catalog, { allowEmpty = false } = {}) {
  if (
    !catalog?.period?.start ||
    !catalog.period.end ||
    !Array.isArray(catalog.items) ||
    (!allowEmpty && !catalog.items.length)
  )
    throw Error("Missing lab catalog");
  const ids = new Set();
  for (const item of catalog.items) {
    if (!/^[a-z0-9][a-z0-9-]*$/.test(item.id) || ids.has(item.id))
      throw Error("Duplicate or invalid lab ID");
    ids.add(item.id);
    for (const key of ["title", "summary"])
      for (const lang of ["zh", "en"])
        if (!item[key]?.[lang]?.trim())
          throw Error("Missing bilingual " + key + ": " + item.id);
    if (!safeLabUrl(item.url) || !item.kind || !item.status)
      throw Error("Missing lab source or classification");
    if (item.date !== null && !/^\d{4}(?:-\d{2}(?:-\d{2})?)?$/.test(item.date))
      throw Error("Invalid lab date");
    const image = typeof item.image === "string" ? item.image : item.image?.src;
    if (image && !safeLabUrl(image)) throw Error("Invalid image URL");
  }
  for (const item of catalog.items)
    for (const link of item.related || []) {
      // A link into another year's wall names that year (2026-09-24: walls
      // became calendar years, and 49 curated links now cross a boundary).
      // That wall is not in this catalog, so the test checks it both ways.
      if (
        (link.year !== undefined ? !/^\d{4}$/.test(link.year) : !ids.has(link.id)) ||
        link.id === item.id ||
        !localText(link.label, "en") ||
        !localText(link.label, "zh")
      )
        throw Error("Invalid lab relationship");
    }
  return catalog;
}
export function labRelations(items, id) {
  const edges = new Map();
  for (const item of items)
    for (const edge of item.related || [])
      if (!edge.year && (item.id === id || edge.id === id)) {
        const other = item.id === id ? edge.id : item.id;
        if (!edges.has(other)) edges.set(other, { ...edge, id: other });
      }
  return [...edges.values()];
}
/** Links from this record into another year's wall. They get no string on
 *  the board (the other paper is not on it); the record's card lists them
 *  and each one opens that year with the record in view. */
export const labCrossLinks = (item) => (item.related || []).filter((edge) => edge.year);
// A theme is no longer a place on the board. Hanging the year on four
// horizontal lanes meant a record's height said "press cutting" while its
// left-right position said "July", and the two readings fought: the first
// half of the year sat on empty cork while March to September piled into one
// knot. The board is a time-ordered grid now, and the theme survives as the
// colour of the pin and as a highlight the reader can call for.
export const LAB_GROUPS = [
  { id: "systems", title: { zh: "記憶與共生", en: "Memory & symbiosis" } },
  { id: "art", title: { zh: "聲音與世界", en: "Sound & worlds" } },
  { id: "exchange", title: { zh: "創作與教學", en: "Practice & teaching" } },
  { id: "echo", title: { zh: "媒體回聲", en: "Press & podcasts" } },
];
export function labGroup(item) {
  // What someone else published about the work is an echo of it, whatever the
  // work was, so the kind decides this lane before any curated override does.
  if (["press", "podcast", "interview"].includes(item.kind)) return "echo";
  if (
    ["webtoe", "openav", "sonarium", "heaven-score", "misread"].includes(
      item.id,
    )
  )
    return "art";
  if (
    [
      "muse",
      "crystal-seed",
      "taiwan-md",
      "secondbrain",
      "open-dictate",
    ].includes(item.id)
  )
    return "systems";
  // A workshop and a written column are both him handing the practice on.
  if (["workshop", "publication"].includes(item.kind)) return "exchange";
  return ["open-source", "prototype", "ongoing-practice"].includes(item.kind)
    ? "systems"
    : ["exhibition", "performance", "music"].includes(item.kind)
      ? "art"
      : "exchange";
}
// Deterministic per-id jitter. The wall must look hand pinned and still be a
// pure function: no Math.random, same catalog in, same nails out.
export function labHash(seed) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967296;
}
export const labImage = (item) =>
  typeof item.image === "string" ? item.image : item.image?.src;
/**
 * Ten kinds of paper, decided by the record itself, never by a dice roll.
 * exhibit   an exhibition print: a larger photograph of a show or a
 *           performance with its wall label (title, date, a few lines about
 *           it) under it
 * polaroid  a photograph of a room that existed
 * capture   a screenshot of an interface he wrote
 * clipping  a press cutting: interview or coverage
 * note      a ruled page torn out of a notebook: writing with no photograph
 * card      an index card: a talk, a course or a workshop with no photograph
 * printout  line-printer paper with its feed holes: code with no screenshot
 * score     a sheet of manuscript paper: a piece of music with no photograph
 * ticket    a ticket stub: an exhibition or a performance with no photograph
 * tag       a kraft evidence tag: an award, a token, anything else filed
 *
 * There were five until 2026-09-24 (哲宇：「有幾年內容看起來少的有點空」「還有
 * 哪些可以怎麼顯示」). Everything without a picture was one of two beige slips,
 * and a year of talks, repositories and piano pieces read as a wall of blank
 * labels. A record with no photograph is still a particular kind of evidence,
 * and the paper it is filed on can say which before a word of it is read.
 */
export function labPaperShape(item) {
  if (["interview", "press", "podcast"].includes(item.kind)) return "clipping";
  const code = ["open-source", "prototype", "ongoing-practice"].includes(
    item.kind,
  );
  // An exhibition or a performance with a photograph is printed larger with
  // its own label (哲宇 2026-09-24：「重要展覽可以有一個特別的大一點點的版型
  // 圖＋標題＋一些描述」, then 「把演出也改大版型」): a show is the record a
  // visitor could have walked into.
  if (labImage(item))
    return code
      ? "capture"
      : ["exhibition", "performance"].includes(item.kind)
        ? "exhibit"
        : "polaroid";
  if (code) return "printout";
  if (item.kind === "publication") return "note";
  if (["talk", "course", "workshop"].includes(item.kind)) return "card";
  if (item.kind === "music") return "score";
  if (["exhibition", "performance"].includes(item.kind)) return "ticket";
  return "tag";
}
/** The closed kind vocabulary (public/ai/lab/schema.json), in words: the
 *  stamp on a sheet and the line on the record card say the same thing. */
export const LAB_KINDS = {
  prototype: { zh: "原型", en: "Prototype" },
  publication: { zh: "文章", en: "Writing" },
  "open-source": { zh: "開源", en: "Open source" },
  exhibition: { zh: "展覽", en: "Exhibition" },
  talk: { zh: "演講", en: "Talk" },
  press: { zh: "報導", en: "Press" },
  course: { zh: "課程", en: "Course" },
  music: { zh: "音樂", en: "Music" },
  podcast: { zh: "節目", en: "Podcast" },
  nft: { zh: "NFT", en: "NFT" },
  workshop: { zh: "工作坊", en: "Workshop" },
  performance: { zh: "演出", en: "Performance" },
  interview: { zh: "訪談", en: "Interview" },
  award: { zh: "獎項", en: "Award" },
  "ongoing-practice": { zh: "持續實驗", en: "Ongoing practice" },
};
/**
 * Size is what the record carries: one cut per kind of paper, never a ladder
 * the layout walks down when it runs out of cork. A sheet is cut once and it
 * keeps that cut: a column of papers each sized to its own contents is what
 * makes the wall read as evenly filled rather than as a heap with gaps.
 *
 * A paper with a picture on it is as tall as its picture plus the strip of
 * card under it: a 3:2 photograph inside a 5% border is 0.60 of the paper's
 * width, and two lines of title with the date above them need 0.42 more. At
 * 280 wide that comes to 300 tall, which is the height of one row.
 */
export const LAB_IMAGE_RATIO = 2 / 3;
export const LAB_IMAGE_PAD = 0.05;
export const LAB_CAPTION_BAND = 0.42;
export const LAB_IMAGE_WIDTH = 280;
/** An exhibition print: a photograph a size up, with a deeper label band
 *  under the picture, where the title, the date and a few lines about the
 *  show are set. */
export const LAB_EXHIBIT_WIDTH = 400;
export const LAB_EXHIBIT_BAND = 0.62;
// The paper without a picture is cut to what it is: an index card is a
// landscape 5 by 3, a ticket is long and low, a sheet of manuscript paper is
// upright. Mixed proportions are most of what makes a column of evidence look
// pinned up rather than printed.
export const LAB_PAPER_SIZES = {
  clipping: [250, 230],
  note: [240, 206],
  card: [258, 172],
  printout: [250, 224],
  score: [236, 252],
  ticket: [274, 140],
  tag: [206, 164],
};
export function labImageHeight(width = LAB_IMAGE_WIDTH) {
  return Math.round(
    width * LAB_IMAGE_PAD +
      width * (1 - 2 * LAB_IMAGE_PAD) * LAB_IMAGE_RATIO +
      width * LAB_CAPTION_BAND,
  );
}
export function labPaperSize(item) {
  const shape = labPaperShape(item);
  if (shape === "exhibit") return [LAB_EXHIBIT_WIDTH, labExhibitHeight()];
  if (shape === "clipping" && labImage(item)) return LAB_CLIPPING_PHOTO;
  if (shape === "polaroid" || shape === "capture")
    return [LAB_IMAGE_WIDTH, labImageHeight()];
  return LAB_PAPER_SIZES[shape];
}
// Where the photograph stops and the caption band starts, as fractions of the
// sheet the DOM layer paints. Derived from the same three numbers the height
// is, so the band can never start in the middle of the picture.
export const LAB_IMAGE_BOX = {
  top: LAB_IMAGE_PAD / (labImageHeight() / LAB_IMAGE_WIDTH),
  height:
    ((1 - 2 * LAB_IMAGE_PAD) * LAB_IMAGE_RATIO) /
    (labImageHeight() / LAB_IMAGE_WIDTH),
};
/**
 * A cutting with a photograph in it: the masthead, then the picture across
 * the column, then the headline and the first lines of the article. Taller
 * than a cutting without one, cut to hold a 3:2 picture at 88% of its width
 * under a masthead of an eighth of its height.
 */
export const LAB_CLIPPING_PHOTO = [264, 336];
export const LAB_CLIPPING_BOX = {
  top: 0.125,
  height: (0.88 * LAB_CLIPPING_PHOTO[0] * LAB_IMAGE_RATIO) / LAB_CLIPPING_PHOTO[1],
};
export function labExhibitHeight(width = LAB_EXHIBIT_WIDTH) {
  return Math.round(
    width * LAB_IMAGE_PAD +
      width * (1 - 2 * LAB_IMAGE_PAD) * LAB_IMAGE_RATIO +
      width * LAB_EXHIBIT_BAND,
  );
}
/** The same two fractions for the exhibition print, read back out of the
 *  stylesheet by the contract test like the photograph's. */
export const LAB_EXHIBIT_BOX = {
  top: LAB_IMAGE_PAD / (labExhibitHeight() / LAB_EXHIBIT_WIDTH),
  height:
    ((1 - 2 * LAB_IMAGE_PAD) * LAB_IMAGE_RATIO) /
    (labExhibitHeight() / LAB_EXHIBIT_WIDTH),
};
/**
 * The theme is the colour of the pin now that it is no longer a lane on the
 * board. Four heads, one per curated theme, the same four the buttons above
 * the wall carry as dots.
 */
export const LAB_PIN_TONES = {
  systems: "#c3322b",
  art: "#1d1b19",
  exchange: "#f1ece0",
  echo: "#3a6ea5",
};
export function labPinTone(item) {
  return labGroup(item);
}
/**
 * Some papers are pinned, some are taped. A polaroid goes up with two strips
 * of tape, a torn note takes a pin through the top: the hash only decides the
 * minority that breaks the habit.
 */
export function labFasten(item) {
  const bias = {
    exhibit: 0.82,
    polaroid: 0.74,
    capture: 0.42,
    clipping: 0.5,
    note: 0.2,
    card: 0.3,
    printout: 0.62,
    score: 0.5,
    ticket: 0.22,
    tag: 0.12,
  }[labPaperShape(item)];
  return labHash(item.id + "fasten") < bias ? "tape" : "pin";
}
/**
 * The deduplicated relations, with the nail each end is tied to. Three places
 * on a sheet can take a string (top centre, top left, top right) and they are
 * handed out two relations at a time, so a paper carrying several relations
 * really does show two strings running into one pin head.
 */
export function labEdges(items) {
  const ids = new Set(items.map((item) => item.id)),
    known = new Set(),
    tally = new Map(),
    edges = [];
  const take = (id) => {
    const used = tally.get(id) || 0;
    tally.set(id, used + 1);
    return Math.floor(used / 2) % 3;
  };
  for (const item of items)
    for (const link of item.related || []) {
      const key = [item.id, link.id].sort().join("~");
      if (!ids.has(link.id) || known.has(key)) continue;
      known.add(key);
      edges.push({
        key,
        from: item.id,
        to: link.id,
        fromSlot: take(item.id),
        toSlot: take(link.id),
        theme: link.type === "theme",
        label: link.label,
      });
    }
  return edges;
}
/** Every nail on every sheet: the one holding it up plus every string's. */
export function labPinSlots(items) {
  const slots = new Map(
    items.map((item) => [
      item.id,
      new Set(labFasten(item) === "pin" ? [0] : []),
    ]),
  );
  for (const edge of labEdges(items)) {
    slots.get(edge.from).add(edge.fromSlot);
    slots.get(edge.to).add(edge.toSlot);
  }
  return slots;
}
// The lamp hangs off the top left of the board. Every paper reads its own
// distance to it, so the bottom right corner of the wall is genuinely dimmer
// than the corner the light falls on. Same numbers for WebGL and for the DOM
// type sitting on top of it, or the band would float off its own paper.
// It hangs off the board that is up, not off a fixed point of the cork: a
// twenty-record year is a smaller board than a fifty-record one, and a lamp
// placed for the big board lit only the cork to the left of the small one.
export function labLamp(board) {
  return {
    x: -board.x - 160,
    y: board.y + 260,
    reach: 2.1 * Math.hypot(board.x, board.y),
  };
}
export function labLampFactor(x, y, lamp) {
  const d = Math.hypot(x - lamp.x, y - lamp.y) / lamp.reach;
  return 1 - 0.28 * Math.min(1, d);
}
/**
 * At most one note per paper, and only on about half of them. A note on every
 * sheet is not annotation, it is wallpaper, and it was the thing making the
 * board look crowded before anything had even moved. A press cutting never
 * gets one: nobody annotates someone else's article on their own wall.
 */
export function labStickyCount(item) {
  if (!(item.keywords || []).length) return 0;
  // A ticket stub is too low to take a note without losing its title.
  if (["clipping", "ticket"].includes(labPaperShape(item))) return 0;
  return labHash(item.id + "sticky") < 0.55 ? 1 : 0;
}
/**
 * Slack of one string. Red yarn on an evidence board is pulled tight between
 * two pins: one to two and a half percent of its length, where the annealed
 * board let it hang by up to seven and a half and bow sideways as well. A
 * loop of string the width of the year is what made the old board read as a
 * net thrown over it; a taut line reads as an argument.
 */
export function labStringSag(key) {
  return 0.01 + labHash(key + "sag") * 0.015;
}

// ---------------------------------------------------------------------------
// Reading order
// ---------------------------------------------------------------------------
/** Date order, undated last, id as the tie so two same-day records never swap. */
export function labOrdered(items) {
  return [...items].sort(
    (a, b) =>
      (a.date || "9999").localeCompare(b.date || "9999") ||
      (a.id < b.id ? -1 : 1),
  );
}
/**
 * The written index under the wall reads the same order backwards: the
 * newest record first, the undated "ongoing" ones at the very top (on the
 * board they hang at the foot of the last column, at the end of the
 * timeline, and the index starts from that end), and two same-day records in
 * the reverse of the wall's id order. One ordering, read from either end;
 * never a second rule that could disagree with the board about who comes
 * before whom. (2026-09-18, 哲宇：「應該要是時間反序排列」)
 */
export function labNewestFirst(items) {
  return labOrdered(items).reverse();
}
/** The month a record is filed under: YYYY-MM, YYYY for a year-only date,
 *  and "ongoing" for the work that has no date to be filed by. */
export const labMonthKey = (item) =>
  item.date ? String(item.date).slice(0, 7) : "ongoing";

// ---------------------------------------------------------------------------
// The rules two sheets are read by
// ---------------------------------------------------------------------------
const boxOf = (n) => ({
  x0: n.x - n.width / 2,
  x1: n.x + n.width / 2,
  y0: n.y - n.height / 2,
  y1: n.y + n.height / 2,
  area: n.width * n.height,
  height: n.height,
});
/**
 * Two papers may tuck under each other by up to 5% of the smaller sheet, and
 * two things may never happen. A sheet may not cover the caption band of the
 * sheet under it, because that band is the only place a record says what it
 * is. And a sheet with no picture may never come over one that has a picture.
 * Which sheet is "under" is decided by the bottom edge, and the stacking order
 * is built from the same comparison, so the geometry and the z order can never
 * disagree. The column flow below leaves cork between every pair, so on the
 * board these are guarantees the test reads back rather than a budget the
 * layout spends.
 */
export const LAB_OVERLAP_MAX = 0.05;
export const LAB_BAND = 0.3;
export function labOverlap(a, b) {
  const A = boxOf(a),
    B = boxOf(b),
    ox = Math.min(A.x1, B.x1) - Math.max(A.x0, B.x0),
    oy = Math.min(A.y1, B.y1) - Math.max(A.y0, B.y0);
  if (ox <= 0 || oy <= 0) return { ratio: 0, buried: false, covered: false };
  const aFirst = A.y0 <= B.y0,
    under = aFirst ? A : B,
    over = aFirst ? B : A,
    underImage = aFirst ? !!a.image : !!b.image,
    overImage = aFirst ? !!b.image : !!a.image;
  return {
    ratio: (ox * oy) / Math.min(A.area, B.area),
    buried: over.y0 < under.y0 + under.height * LAB_BAND,
    covered: underImage && !overImage,
  };
}

// ---------------------------------------------------------------------------
// The board, solved: simulated quantum annealing
// ---------------------------------------------------------------------------
/**
 * 2026-09-24 (哲宇：「排的好像有一點沒那麼好看」, then, of a board pinned in
 * tidy columns, 「看起來太死板板了，參考量子退火演算法升級，要更有機一些」 with
 * three photographs of investigation walls). The walls in those photographs
 * are not ruled and they are not heaped. Paper covers the cork evenly; the
 * sheets that carry the most string sit where the eye lands and the red lines
 * run out from them; related papers stand near each other, so a string is a
 * short line and not a loop across the room.
 *
 * The previous annealer knew most of that and still left knots beside bare
 * cork, because its board was a fixed size for any year and its papers were
 * drawn together by a well that clotted them. Three things changed.
 *
 * The cork is cut to the year. Its area is what the papers need with a hand's
 * width of cork round each, in the frame's own proportions, so a twenty-record
 * year is a smaller board drawn larger rather than a big board with holes.
 *
 * The papers push each other apart until the cork between neighbours is even,
 * and the walls of the board push back; with no attraction but the strings,
 * the year spreads over its board the way a gas fills a jar. The strings pull
 * related records together and are charged for every other string they cross,
 * and the year leans left to right by rank, hard enough that two records two
 * months apart are never read backwards.
 *
 * And the search is a simulated quantum annealer rather than a single walk.
 * Six replicas of the board - the Trotter slices of a path-integral Monte
 * Carlo - anneal side by side, each tied to its two neighbours by a coupling
 * that plays the transverse field: loose at first, so the replicas explore
 * different arrangements, then tightened until they agree. A replica that has
 * found a better neighbourhood drags the others toward it through the
 * coupling, and swap moves let two papers change places outright, tunnelling
 * through the wall of overlaps a slide would have to climb. The board is the
 * lowest-energy replica, pried apart where it still breaks a rule.
 *
 * Nothing here touches Math.random. The dice are a mulberry32 seeded off the
 * catalog's ids and the schedule is fixed, so the same catalog always hangs
 * the same way: on the server, in the test, and in every reader's browser.
 */
/** A sheet with a picture on it is this tall (labImageHeight). */
export const LAB_ROW_H = 300;
/** The frame the wall is drawn in, width over height. */
export const LAB_ASPECT = 1.7;
/** How far off true a sheet may hang, in radians either way. */
export const LAB_TILT = 0.09;
/** Two records this many days apart may never be hung the wrong way round. */
export const LAB_MONOTONE = 60;
/** The rail of month tabs above the board and the strip for the case label
 *  under it are part of what the camera frames. */
export const LAB_RAIL_GAP = 40;
export const LAB_CARD_STRIP = 118;
/** Two month tabs never hang closer than this, unless the rail is too short
 *  for all of them: a school wall runs over forty months, and then the gap
 *  closes to what the board's width allows, down to `LAB_TAB_MIN`. */
export const LAB_TAB_GAP = 150;
export const LAB_TAB_MIN = 90;
/** The cork each sheet is given on a side when the board is cut, and how much
 *  looser than a perfect packing the cut is. */
const CORK_PER_SHEET = 58;
const PACKING = 1.16;
/** Every weight in one table, because tuning a wall means tuning these. */
export const LAB_ENERGY = {
  // Paper over paper, as the squared share of the smaller sheet.
  overlap: 90000,
  // Three times over when a sheet with no picture lands on a photograph.
  image: 3,
  // A sheet lying across the caption band of the sheet under it.
  band: 60000,
  // Cork between neighbours: two sheets closer than `reach` are pushed apart.
  // Each pair has its own reach, a share of it off the pair's hash, and two
  // records tied by a string under half of theirs: an even push everywhere
  // gives an even lattice, and a hand does not pin one. About a third of the
  // pairs get a reach below zero, which lets one sheet tuck a little under
  // another (哲宇 2026-09-24：「稍微有一點點重疊也好看沒關係」); how far is
  // still the three rules' call - never over a caption band, never a memo on
  // a photograph, never past five percent.
  repel: 4,
  reach: 50,
  spread: [-0.8, 1.5],
  tied: 0.22,
  // Off the cork.
  wall: 60,
  // A red string is short, and pulls hard enough that the records it ties
  // gather into groups (哲宇 2026-09-24：「根據關係連線稍微拉近一點點，比較會有
  // 分群的感覺」)...
  spring: 0.02,
  // ...and does not cross another one.
  cross: 4200,
  // The year leans left to right by rank, lightly, so a group can gather
  // across a month or two...
  time: 0.0035,
  // ...and two months apart it is never read backwards: charged by the unit
  // and by the square, because a cheap reversal left for the correction to
  // undo is a long shove through everything in between.
  order: 40,
  orderSquare: 0.6,
  // A record carrying three strings or more hangs near the board's middle
  // line, so its strings run out above and below it.
  hub: 0.03,
};
/** The annealing schedule. */
export const LAB_QUANTUM = {
  // Trotter slices: replicas of the one board.
  replicas: 6,
  // Proposals per sheet per replica.
  sweeps: 64,
  // Temperature, first sweep to last.
  hot: 3000,
  cold: 10,
  // The replica coupling (the transverse field, read backwards): loose while
  // the replicas explore, tight when they have to agree.
  loose: 0.0008,
  tight: 0.8,
  // How far one slide reaches, first sweep to last.
  far: 260,
  near: 5,
  // Shares of the proposals that tunnel (swap two papers) and that re-pin a
  // sheet at another angle; the rest slide.
  swap: 0.1,
  turn: 0.08,
  // Checkpoints before this share of the run are not candidates.
  settled: 0.55,
};
/**
 * A record that carries three strings or more is cut a size up: the paper the
 * eye should land on first is the one the most red runs out of. And no two
 * sheets are quite the same cut, because no two were printed on the same
 * printer: nine percent either way, off the record's own hash. Sheets of one
 * size settle into a lattice under an even push; sheets of slightly different
 * sizes settle into a wall.
 */
export function labScale(item, links) {
  // An exhibition print is already the large one.
  const hub =
    links >= 3 && labPaperShape(item) !== "exhibit"
      ? 1 + 0.11 * Math.min(2.5, links - 2)
      : 1;
  return hub * (0.91 + 0.18 * labHash(item.id + "cut"));
}

/** The dice. Same seed, same board, on every machine. */
export function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
/** The catalog's own ids, hashed: a new record reshuffles the wall, a reload
 *  never does. */
export function labSeed(items) {
  const text = items.map((item) => item.id).join("|");
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
const dayOf = (date) =>
  date == null ? null : Date.parse(String(date).padEnd(10, "-01")) / 86400000;
// Two segments cross (shared endpoints excluded by the caller).
function crosses(ax, ay, bx, by, cx, cy, dx, dy) {
  const d1 = (dx - cx) * (ay - cy) - (dy - cy) * (ax - cx),
    d2 = (dx - cx) * (by - cy) - (dy - cy) * (bx - cx),
    d3 = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax),
    d4 = (bx - ax) * (dy - ay) - (by - ay) * (dx - ax);
  return d1 * d2 < 0 && d3 * d4 < 0;
}

/**
 * The year as arrays: sizes, dates, strings, the board it is cut to and
 * where each sheet starts. One of these per catalog; the replicas are copies
 * of its x, y and angle.
 */
function prepare(items, room = 1) {
  const order = labOrdered(items),
    n = order.length,
    index = new Map(order.map((item, i) => [item.id, i])),
    links = order.map((item) => labRelations(items, item.id).length),
    w = new Float64Array(n),
    h = new Float64Array(n),
    image = new Uint8Array(n),
    day = order.map((item) => dayOf(item.date ?? null)),
    frozen = new Uint8Array(n),
    edges = [],
    incident = order.map(() => []),
    seen = new Set();
  order.forEach((item, i) => {
    const [pw, ph] = labPaperSize(item),
      k = labScale(item, links[i]);
    w[i] = Math.round(pw * k);
    h[i] = Math.round(ph * k);
    image[i] = labImage(item) ? 1 : 0;
    frozen[i] = item.date == null ? 1 : 0;
  });
  for (const item of items)
    for (const link of item.related || []) {
      if (link.year) continue;
      const a = index.get(item.id),
        b = index.get(link.id);
      if (a === undefined || b === undefined) continue;
      const key = a < b ? `${a}~${b}` : `${b}~${a}`;
      if (seen.has(key)) continue;
      seen.add(key);
      incident[a].push(edges.length);
      incident[b].push(edges.length);
      edges.push([a, b]);
    }
  // How close each pair of sheets will stand (LAB_ENERGY.reach, spread, tied).
  const reach = new Float64Array(n * n),
    [lowest, highest] = LAB_ENERGY.spread;
  for (let i = 0; i < n; i++)
    for (let j = i + 1; j < n; j++) {
      const a = order[i].id,
        b = order[j].id,
        k =
          (lowest + (highest - lowest) * labHash(a < b ? a + "~" + b : b + "~" + a)) *
          (seen.has(`${i}~${j}`) ? LAB_ENERGY.tied : 1);
      reach[i * n + j] = reach[j * n + i] = LAB_ENERGY.reach * k;
    }
  // The cork: what the sheets need with a hand's width round each, in the
  // frame's proportions once the rail above and the card strip below are
  // counted in.
  let area = 0,
    widest = 0;
  for (let i = 0; i < n; i++) {
    area += (w[i] + CORK_PER_SHEET) * (h[i] + CORK_PER_SHEET);
    widest = Math.max(widest, w[i], h[i]);
  }
  area *= PACKING * room;
  const extra = LAB_RAIL_GAP + 42 + LAB_CARD_STRIP,
    a = LAB_ASPECT,
    tall = Math.max(
      widest + 40,
      (-a * extra + Math.sqrt(a * a * extra * extra + 4 * a * area)) / (2 * a),
    ),
    wide = Math.max(widest + 40, area / tall),
    board = { x: wide / 2, y: tall / 2 };
  // Where the year leans each sheet: its rank along the width. The undated
  // work is the right-hand edge and every dated sheet stays left of it.
  const dated = order.filter((item) => item.date != null).length,
    open = n - dated,
    reserve = open ? 60 : 0,
    lean = new Float64Array(n),
    right = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    right[i] = frozen[i] ? board.x : board.x - reserve;
    lean[i] = frozen[i]
      ? board.x - w[i] / 2
      : -board.x + ((i + 0.5) / Math.max(1, dated)) * (2 * board.x - reserve);
  }
  return {
    order,
    n,
    index,
    links,
    w,
    h,
    image,
    day,
    frozen,
    edges,
    incident,
    board,
    lean,
    right,
    reach,
  };
}

/** One replica: positions, angles and the rotated half-extents the energy
 *  measures, grown by a hairline so "just touching" still shows cork. */
function replica(year, random) {
  const { n, w, h, board, frozen, order } = year,
    x = new Float64Array(n),
    y = new Float64Array(n),
    rot = new Float64Array(n),
    ex = new Float64Array(n),
    ey = new Float64Array(n),
    // The time-ordered grid the year starts from: columns by rank, filled
    // top to bottom, shaken.
    columns = Math.max(1, Math.round(Math.sqrt((n * board.x) / board.y))),
    rows = Math.ceil(n / columns);
  for (let i = 0; i < n; i++) {
    const col = Math.floor(i / rows),
      row = i % rows;
    x[i] = frozen[i]
      ? board.x - w[i] / 2
      : -board.x + ((col + 0.5) * 2 * board.x) / columns +
        (random() * 2 - 1) * 60;
    y[i] = board.y - ((row + 0.5) * 2 * board.y) / rows + (random() * 2 - 1) * 60;
    rot[i] =
      (labHash(order[i].id + "r") * 2 - 1) * LAB_TILT * (year.image[i] ? 0.75 : 1);
    extents(year, { x, y, rot, ex, ey }, i);
  }
  return { x, y, rot, ex, ey };
}
function extents(year, r, i) {
  const c = Math.abs(Math.cos(r.rot[i])),
    s = Math.abs(Math.sin(r.rot[i]));
  r.ex[i] = (year.w[i] * c + year.h[i] * s) / 2 + 6;
  r.ey[i] = (year.w[i] * s + year.h[i] * c) / 2 + 6;
}

function pairEnergy(year, r, i, j) {
  const E = LAB_ENERGY,
    reach = year.reach[i * year.n + j],
    dx = r.x[i] - r.x[j],
    dy = r.y[i] - r.y[j],
    sx = Math.abs(dx) - (r.ex[i] + r.ex[j]),
    sy = Math.abs(dy) - (r.ey[i] + r.ey[j]),
    gap = Math.max(sx, sy);
  let e = 0;
  if (gap < reach) e += E.repel * (reach - gap) * (reach - gap);
  if (sx < 0 && sy < 0) {
    const share = (sx * sy) / Math.min(r.ex[i] * r.ey[i], r.ex[j] * r.ey[j]) / 4,
      iUnder = r.y[i] - r.ey[i] <= r.y[j] - r.ey[j],
      under = iUnder ? i : j,
      over = iUnder ? j : i;
    e +=
      E.overlap *
      share *
      share *
      (year.image[under] && !year.image[over] ? E.image : 1);
    const sunk =
      r.y[under] - r.ey[under] + 2 * r.ey[under] * LAB_BAND - (r.y[over] - r.ey[over]);
    if (sunk > 0) {
      const part = sunk / (2 * r.ey[under]);
      e += E.band * part * part;
    }
  }
  const di = year.day[i],
    dj = year.day[j];
  if (di !== null && dj !== null && Math.abs(di - dj) >= LAB_MONOTONE) {
    const early = di < dj ? i : j,
      late = early === i ? j : i;
    const back = r.x[early] - r.x[late] + 2;
    if (back > 0) e += E.order * back + E.orderSquare * back * back;
  }
  return e;
}
function nodeEnergy(year, r, i) {
  const E = LAB_ENERGY,
    { board } = year,
    ox = Math.max(
      -board.x - (r.x[i] - r.ex[i]),
      r.x[i] + r.ex[i] - year.right[i],
      0,
    ),
    oy = Math.abs(r.y[i]) + r.ey[i] - board.y;
  let e = E.time * (r.x[i] - year.lean[i]) * (r.x[i] - year.lean[i]);
  if (ox > 0) e += E.wall * ox * ox;
  if (oy > 0) e += E.wall * oy * oy;
  if (year.links[i] >= 3) e += E.hub * r.y[i] * r.y[i];
  return e;
}
// The strings of sheet i: their length, and every other string they cross.
function stringEnergy(year, r, i) {
  const E = LAB_ENERGY;
  let e = 0;
  for (const k of year.incident[i]) {
    const [a, b] = year.edges[k],
      dx = r.x[a] - r.x[b],
      dy = r.y[a] - r.y[b];
    e += E.spring * (dx * dx + dy * dy);
    for (let m = 0; m < year.edges.length; m++) {
      if (m === k) continue;
      const [c, d] = year.edges[m];
      if (c === a || c === b || d === a || d === b) continue;
      if (crosses(r.x[a], r.y[a], r.x[b], r.y[b], r.x[c], r.y[c], r.x[d], r.y[d]))
        e += E.cross;
    }
  }
  return e;
}
function localEnergy(year, r, i) {
  let e = nodeEnergy(year, r, i) + stringEnergy(year, r, i);
  for (let j = 0; j < year.n; j++) if (j !== i) e += pairEnergy(year, r, i, j);
  return e;
}
/** The energy of one replica on its own: what the board is judged by. */
function boardEnergy(year, r) {
  let e = 0;
  for (let i = 0; i < year.n; i++) {
    e += nodeEnergy(year, r, i);
    for (let j = i + 1; j < year.n; j++) e += pairEnergy(year, r, i, j);
  }
  // Each string's length once, each crossing once.
  let strings = 0;
  for (let i = 0; i < year.n; i++) strings += stringEnergy(year, r, i);
  // stringEnergy counts a string's length from both of its ends and a
  // crossing from all four: halve the springs, quarter the crossings.
  let length = 0;
  for (const [a, b] of year.edges) {
    const dx = r.x[a] - r.x[b],
      dy = r.y[a] - r.y[b];
    length += LAB_ENERGY.spring * (dx * dx + dy * dy);
  }
  const crossings = (strings - 2 * length) / 4;
  return e + length + crossings;
}
// The coupling of sheet i in replica p to the same sheet in its two
// neighbouring replicas.
function coupling(replicas, p, i, field) {
  const count = replicas.length;
  if (count < 2) return 0;
  const r = replicas[p];
  let e = 0;
  for (const q of count === 2 ? [(p + 1) % count] : [(p + 1) % count, (p + count - 1) % count]) {
    const o = replicas[q],
      dx = r.x[i] - o.x[i],
      dy = r.y[i] - o.y[i];
    e += field * (dx * dx + dy * dy);
  }
  return e;
}

/**
 * The year, made a rule rather than a preference: every paper is squeezed
 * between the rightmost paper two months older than it and the leftmost paper
 * two months younger (prefix and suffix extremes), so the whole board settles
 * into date order at the only scale the reader can see.
 */
function enforceTime(year, r) {
  const dated = [];
  for (let i = 0; i < year.n; i++) if (year.day[i] !== null) dated.push(i);
  const count = dated.length;
  if (!count) return;
  const prefix = new Float64Array(count),
    suffix = new Float64Array(count);
  let run = -Infinity;
  for (let k = 0; k < count; k++) prefix[k] = run = Math.max(run, r.x[dated[k]]);
  run = Infinity;
  for (let k = count - 1; k >= 0; k--) suffix[k] = run = Math.min(run, r.x[dated[k]]);
  let low = 0,
    high = 0;
  for (let k = 0; k < count; k++) {
    const today = year.day[dated[k]];
    while (year.day[dated[low]] <= today - LAB_MONOTONE) low++;
    if (high < k) high = k;
    while (high < count && year.day[dated[high]] < today + LAB_MONOTONE) high++;
    const floor = low > 0 ? prefix[low - 1] + 1 : -Infinity,
      ceiling = high < count ? suffix[high] - 1 : Infinity,
      i = dated[k];
    r.x[i] =
      floor > ceiling
        ? (floor + ceiling) / 2
        : Math.max(floor, Math.min(ceiling, r.x[i]));
  }
}
/** Back inside the cork, and never right of the work with no end date. */
function clamp(year, r) {
  const { board } = year;
  for (let i = 0; i < year.n; i++) {
    if (!year.frozen[i])
      r.x[i] = Math.max(
        -board.x + year.w[i] / 2,
        Math.min(year.right[i] - year.w[i] / 2, r.x[i]),
      );
    r.y[i] = Math.max(
      -board.y + year.h[i] / 2,
      Math.min(board.y - year.h[i] / 2, r.y[i]),
    );
  }
}
/**
 * The correction that always runs, on the real sheets rather than the padded
 * boxes: every pair still breaking a rule (more than a sliver of overlap, a
 * caption band covered, a memo over a photograph) is pried apart along the
 * cheaper axis, then everything goes back inside the cork and into date order.
 */
const SETTLE_TARGET = 0.03,
  BAND_SLACK = 6;
function pushApart(year, r, a, b) {
  const { w, h } = year,
    ox = (w[a] + w[b]) / 2 - Math.abs(r.x[a] - r.x[b]),
    oy = (h[a] + h[b]) / 2 - Math.abs(r.y[a] - r.y[b]);
  if (ox <= 0 || oy <= 0) return null;
  const aUnder = r.y[a] - h[a] / 2 <= r.y[b] - h[b] / 2,
    under = aUnder ? a : b,
    over = aUnder ? b : a,
    covered = !!year.image[under] && !year.image[over],
    floor = r.y[under] - h[under] / 2 + h[under] * LAB_BAND + BAND_SLACK,
    buried = r.y[over] - h[over] / 2 < floor,
    minArea = Math.min(w[a] * h[a], w[b] * h[b]),
    ratio = (ox * oy) / minArea;
  if (!covered && !buried && ratio <= SETTLE_TARGET) return null;
  let dx = ox + 1,
    dy = oy + 1;
  if (!covered) {
    const room = SETTLE_TARGET * minArea;
    dx = buried ? ox + 1 : Math.max(0, ox - room / oy);
    dy = Math.max(
      buried ? floor - (r.y[over] - h[over] / 2) : 0,
      ratio > SETTLE_TARGET ? Math.max(0, oy - room / ox) : 0,
    );
  }
  return dx <= dy
    ? { axis: "x", amount: dx, first: r.x[a] <= r.x[b] ? a : b }
    : { axis: "y", amount: dy, first: under };
}
function settle(year, r, passes) {
  const { n } = year;
  for (let pass = 0; pass < passes; pass++) {
    let moved = 0;
    for (let i = 0; i < n; i++)
      for (let j = i + 1; j < n; j++) {
        const push = pushApart(year, r, i, j);
        if (!push) continue;
        moved++;
        const low = push.first,
          high = low === i ? j : i,
          lowFixed = push.axis === "x" && year.frozen[low],
          highFixed = push.axis === "x" && year.frozen[high],
          share = lowFixed && highFixed ? [0, 0] : lowFixed ? [0, 1] : highFixed ? [1, 0] : [0.5, 0.5],
          step = push.amount * 0.55,
          axis = push.axis === "x" ? r.x : r.y;
        axis[low] -= step * share[0];
        axis[high] += step * share[1];
      }
    clamp(year, r);
    for (let k = 0; k < 3; k++) enforceTime(year, r);
    clamp(year, r);
    if (!moved) break;
  }
}

function anneal(items, room = 1) {
  const year = prepare(items, room),
    { n } = year,
    Q = LAB_QUANTUM,
    random = mulberry32(labSeed(items)),
    replicas = [];
  for (let p = 0; p < Q.replicas; p++) replicas.push(replica(year, random));
  if (n < 2) {
    clamp(year, replicas[0]);
    return { year, best: replicas[0], sweeps: 0, energy: 0, initial: 0 };
  }
  const initial = boardEnergy(year, replicas[0]);
  const copy = (r) => ({
    x: Float64Array.from(r.x),
    y: Float64Array.from(r.y),
    rot: Float64Array.from(r.rot),
    ex: Float64Array.from(r.ex),
    ey: Float64Array.from(r.ey),
  });
  let best = null;
  for (let sweep = 0; sweep < Q.sweeps; sweep++) {
    const t = Q.sweeps > 1 ? sweep / (Q.sweeps - 1) : 1,
      T = Q.hot * Math.pow(Q.cold / Q.hot, t),
      field = Q.loose * Math.pow(Q.tight / Q.loose, t),
      reach = Q.far * Math.pow(Q.near / Q.far, t);
    for (let p = 0; p < replicas.length; p++) {
      const r = replicas[p];
      for (let move = 0; move < n; move++) {
        const k = Math.floor(random() * n),
          roll = random();
        if (roll < Q.swap) {
          // Tunnelling: two papers trade places outright.
          const l = Math.floor(random() * n);
          if (l === k || year.frozen[k] || year.frozen[l]) continue;
          const before =
            localEnergy(year, r, k) +
            localEnergy(year, r, l) -
            pairEnergy(year, r, k, l) +
            coupling(replicas, p, k, field) +
            coupling(replicas, p, l, field);
          const kx = r.x[k],
            ky = r.y[k];
          r.x[k] = r.x[l];
          r.y[k] = r.y[l];
          r.x[l] = kx;
          r.y[l] = ky;
          const after =
            localEnergy(year, r, k) +
            localEnergy(year, r, l) -
            pairEnergy(year, r, k, l) +
            coupling(replicas, p, k, field) +
            coupling(replicas, p, l, field);
          const delta = after - before;
          if (delta > 0 && random() >= Math.exp(-delta / T)) {
            r.x[l] = r.x[k];
            r.y[l] = r.y[k];
            r.x[k] = kx;
            r.y[k] = ky;
          }
        } else if (roll < Q.swap + Q.turn) {
          const before = localEnergy(year, r, k),
            keep = r.rot[k];
          r.rot[k] = (random() * 2 - 1) * LAB_TILT * (year.image[k] ? 0.75 : 1);
          extents(year, r, k);
          const delta = localEnergy(year, r, k) - before;
          if (delta > 0 && random() >= Math.exp(-delta / T)) {
            r.rot[k] = keep;
            extents(year, r, k);
          }
        } else {
          const before = localEnergy(year, r, k) + coupling(replicas, p, k, field),
            kx = r.x[k],
            ky = r.y[k];
          if (!year.frozen[k]) r.x[k] += (random() * 2 - 1) * reach;
          r.y[k] += (random() * 2 - 1) * reach;
          const delta =
            localEnergy(year, r, k) + coupling(replicas, p, k, field) - before;
          if (delta > 0 && random() >= Math.exp(-delta / T)) {
            r.x[k] = kx;
            r.y[k] = ky;
          }
        }
      }
    }
    // Remember the best single replica seen once the run has settled: the
    // replicas are judged on the board alone, never on the coupling.
    if (t >= Q.settled || sweep === Q.sweeps - 1)
      for (const r of replicas) {
        const e = boardEnergy(year, r);
        if (!best || e < best.e) best = { e, r: copy(r) };
      }
  }
  const board = best.r,
    annealed = best.e;
  clamp(year, board);
  settle(year, board, 400);
  return {
    year,
    best: board,
    sweeps: Q.sweeps,
    initial,
    annealed,
    energy: boardEnergy(year, board),
  };
}

/** Pairs still breaking one of the three rules the test reads back. */
function violations(year, r) {
  let count = 0;
  for (let i = 0; i < year.n; i++)
    for (let j = i + 1; j < year.n; j++) {
      const a = { x: r.x[i], y: r.y[i], width: year.w[i], height: year.h[i], image: year.image[i] },
        b = { x: r.x[j], y: r.y[j], width: year.w[j], height: year.h[j], image: year.image[j] },
        rule = labOverlap(a, b);
      if (rule.ratio > LAB_OVERLAP_MAX || rule.buried || rule.covered) count++;
    }
  return count;
}
/** How much more cork a year gets each time its board still breaks a rule
 *  after the correction, and how many times it may ask. */
const ROOMIER = [1, 1.08, 1.17, 1.27, 1.4];

// One board per catalog. Every caller - the wall, the month rail, the DOM
// fallback in the Vue layer, the test - asks for the same array of items, so
// the annealing is paid for once.
const solved = new WeakMap();
export function labSolve(items) {
  let result = solved.get(items);
  if (!result) {
    const started =
      typeof performance === "object" ? performance.now() : Date.now();
    // A board that cannot be pried apart without breaking a rule is cut
    // again with more cork, rather than shipped with a covered caption.
    let run, room = 0;
    for (; room < ROOMIER.length; room++) {
      run = anneal(items, ROOMIER[room]);
      if (!violations(run.year, run.best)) break;
    }
    room = Math.min(room, ROOMIER.length - 1);
    const { year, best, sweeps, initial, annealed, energy } = run;
    result = {
      nodes: year.order.map((item, i) => ({
        item,
        id: item.id,
        key: labMonthKey(item),
        links: year.links[i],
        scale: labScale(item, year.links[i]),
        image: !!year.image[i],
        w: year.w[i],
        h: year.h[i],
        x: best.x[i],
        y: best.y[i],
        rot: best.rot[i],
      })),
      board: { ...year.board },
      room: ROOMIER[room],
      sweeps,
      replicas: LAB_QUANTUM.replicas,
      initial,
      annealed,
      energy,
    };
    result.ms =
      (typeof performance === "object" ? performance.now() : Date.now()) -
      started;
    solved.set(items, result);
  }
  return result;
}
/** The half-extents of the cork a year hangs on. Takes the catalog; a bare
 *  count is an estimate for tools that only know how many records there are. */
export function labBoard(items) {
  if (Array.isArray(items)) return labSolve(items).board;
  const count = Math.max(1, Number(items) || 1),
    area = count * 300 * 290 * PACKING,
    tall = Math.sqrt(area / LAB_ASPECT);
  return { x: (area / tall) / 2, y: tall / 2 };
}
/**
 * The board the rest of the app sees: the annealed coordinates, plus the
 * stacking order, read off the same bottom-edge comparison the overlap rule
 * uses, so the geometry and the z order can never disagree.
 */
export function labLayout(items) {
  const { nodes, board } = labSolve(items),
    lamp = labLamp(board),
    placed = [...nodes].sort(
      (a, b) =>
        a.y - a.h / 2 - (b.y - b.h / 2) ||
        b.w * b.h - a.w * a.h ||
        (a.id < b.id ? -1 : 1),
    );
  return placed.map((node, position) => ({
    item: node.item,
    id: node.id,
    group: labGroup(node.item),
    shape: labPaperShape(node.item),
    fasten: labFasten(node.item),
    pin: labPinTone(node.item),
    links: node.links,
    scale: node.scale,
    x: node.x,
    y: node.y,
    z: position * 0.9 - 12,
    layer: position,
    lamp: labLampFactor(node.x, node.y, lamp),
    width: node.w,
    height: node.h,
    rotation: node.rot,
    image: labImage(node.item),
    dark: false,
  }));
}
/**
 * Month tabs hang on the rail above the board, each over the middle of its
 * own month's papers, not over the leftmost of them: a month the annealer has
 * spread across the cork is found where most of it is. Two tabs never hang
 * closer than a tab's width, and the rail always reads left to right; where
 * a busy stretch pushes tabs off their months the row is centred back over
 * the stretch rather than all pushed to the right.
 */
export function labMonths(items) {
  const { nodes, board } = labSolve(items),
    groups = new Map();
  for (const node of nodes) {
    if (!groups.has(node.key)) groups.set(node.key, []);
    groups.get(node.key).push(node);
  }
  // The rail runs the width of the board, a tab's half-width in from each
  // end, and the tabs share it.
  const low = -board.x + 40,
    high = board.x - 40,
    // Tabs may take up to six tenths of the rail at the full gap; past that
    // they close up toward the minimum, so a crowded rail can still bunch
    // where its months bunch instead of being spread evenly end to end.
    gap = Math.max(
      LAB_TAB_MIN,
      Math.min(LAB_TAB_GAP, (0.6 * (high - low)) / Math.max(1, groups.size)),
    );
  const top = Math.max(...nodes.map((node) => node.y + node.h / 2)),
    rail = top + LAB_RAIL_GAP,
    marks = [...groups.entries()].map(([key, members]) => ({
      key,
      want: members.reduce((sum, node) => sum + node.x, 0) / members.length,
    }));
  // Tabs in order, a tab's width apart: pool the ones that collide into runs
  // and centre each run over what its months want.
  const runs = [];
  for (const mark of marks) {
    runs.push({ marks: [mark], centre: mark.want });
    for (;;) {
      const last = runs[runs.length - 1],
        prev = runs[runs.length - 2];
      if (!prev) break;
      const prevEnd = prev.centre + ((prev.marks.length - 1) * gap) / 2,
        lastStart = last.centre - ((last.marks.length - 1) * gap) / 2;
      if (lastStart - prevEnd >= gap - 1e-9) break;
      const merged = [...prev.marks, ...last.marks],
        centre =
          merged.reduce((sum, m, i) => sum + m.want - i * gap, 0) / merged.length +
          ((merged.length - 1) * gap) / 2;
      runs.splice(runs.length - 2, 2, { marks: merged, centre });
    }
  }
  const placed = [];
  for (const run of runs)
    run.marks.forEach((mark, i) =>
      placed.push({
        ...mark,
        x: run.centre + (i - (run.marks.length - 1) / 2) * gap,
      }),
    );
  // Then onto the rail: a run centred past either end of the board is walked
  // back onto it, keeping the gap, so no tab hangs off the cork.
  for (let i = 0; i < placed.length; i++)
    placed[i].x = Math.max(placed[i].x, i ? placed[i - 1].x + gap : low);
  for (let i = placed.length - 1; i >= 0; i--)
    placed[i].x = Math.min(
      placed[i].x,
      i < placed.length - 1 ? placed[i + 1].x - gap : high,
    );
  let lastYear = null;
  return placed.map((mark, position) => {
    const [year, month] = mark.key.split("-").map(Number),
      newYear = Boolean(year) && year !== lastYear;
    if (year) lastYear = year;
    return {
      key: mark.key,
      index: position,
      year: year || null,
      month: month || null,
      ongoing: mark.key === "ongoing",
      // A record dated to the year only hangs under a tab of its own.
      yearOnly: mark.key !== "ongoing" && !month,
      // The first tab of the run and every change of year print the year as
      // well; a January straight after the year's own tab does not print it
      // twice (「2025 · 全年」「2025 · 01」 side by side, 2026-09-24).
      first: position === 0 || newYear,
      rail: true,
      gap,
      x: mark.x,
      y: rail,
    };
  });
}
/**
 * The outermost paper edges, plus the rail the month tabs hang from and the
 * strip of cork under the bottom row where the case label is nailed. The
 * camera frames this and nothing else.
 */
export function labBounds(layout) {
  let x0 = Infinity,
    x1 = -Infinity,
    y0 = Infinity,
    y1 = -Infinity;
  for (const node of layout) {
    x0 = Math.min(x0, node.x - node.width / 2);
    x1 = Math.max(x1, node.x + node.width / 2);
    y0 = Math.min(y0, node.y - node.height / 2);
    y1 = Math.max(y1, node.y + node.height / 2);
  }
  if (!Number.isFinite(x0))
    return {
      x0: -100,
      x1: 100,
      y0: -100,
      y1: 100,
      rail: 100,
      floor: -100,
      top: 100,
    };
  const rail = y1 + LAB_RAIL_GAP;
  return {
    x0,
    x1,
    y0,
    y1,
    rail,
    floor: y0 - LAB_CARD_STRIP,
    top: rail + 42,
  };
}
